/**
 * Animates the numeric part of a display string ("$12,480.55", "62%") from
 * zero up to its final value on mount, preserving any currency symbol,
 * thousands separators and decimal places.
 *
 * Falls back to the raw value when the performance profile has disabled
 * motion (the `no-animations` class) and when the string holds no number.
 */

import { useEffect, useRef, useState } from "react";

const NUMBER_PATTERN = /-?\d[\d,]*\.?\d*/;

function motionDisabled(): boolean {
  if (typeof document === "undefined") return true;
  return document.documentElement.classList.contains("no-animations");
}

export function useCountUp(value: string, duration = 750): string {
  const [display, setDisplay] = useState(value);
  const frame = useRef<number | undefined>(undefined);

  useEffect(() => {
    const match = value.match(NUMBER_PATTERN);
    const target = match ? Number(match[0].replace(/,/g, "")) : Number.NaN;

    if (!match || !Number.isFinite(target) || motionDisabled()) {
      setDisplay(value);
      return;
    }

    const decimals = match[0].includes(".") ? match[0].split(".")[1].length : 0;
    const started = performance.now();

    const tick = (now: number): void => {
      const progress = Math.min((now - started) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = target * eased;
      setDisplay(
        value.replace(
          match[0],
          current.toLocaleString("en-US", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          }),
        ),
      );
      if (progress < 1) frame.current = requestAnimationFrame(tick);
    };

    frame.current = requestAnimationFrame(tick);
    return () => {
      if (frame.current !== undefined) cancelAnimationFrame(frame.current);
    };
  }, [value, duration]);

  return display;
}
